import { useEffect, useState, useCallback } from "react";
import api, { errorMessage } from "../lib/api";
import { ActionButton, GhostButton, PillBadge } from "./Primitives";
import { MapPin, Radio, Check, AlertOctagon, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmtRelative(iso) {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

function nextDueTone(stop) {
  if (!stop.last_visit_at) return "amber";
  if (!stop.next_due_at) return "default";
  const due = new Date(stop.next_due_at).getTime();
  const now = Date.now();
  if (now > due) return "red"; // overdue
  if (due - now < 5 * 60 * 1000) return "amber"; // due soon
  return "green";
}

/**
 * Tour stops panel shown inside the Officer Portal during an active shift.
 *
 * Props:
 *   shiftId: string  — the live shift the guard is clocked into
 *   loc: { lat, lng } — current GPS reading (used for GPS visits)
 */
export default function TourStopsPanel({ shiftId, loc }) {
  const [stops, setStops] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nfcShownFor, setNfcShownFor] = useState(null);
  const [nfcTag, setNfcTag] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/portal/tour-stops", { params: { shift_id: shiftId } });
      setStops(r.data || []);
    } catch (e) { toast.error(errorMessage(e, "Failed to load tour stops")); }
    finally { setLoading(false); }
  }, [shiftId]);
  useEffect(() => { load(); }, [load]);

  const visitGps = async (stop) => {
    if (!loc) { toast.error("Waiting for GPS — allow location access."); return; }
    setBusy(`${stop.id}:gps`);
    try {
      await api.post("/portal/tour-visits", { shift_id: shiftId, stop_id: stop.id, method: "gps", lat: loc.lat, lng: loc.lng });
      toast.success(`Visited ${stop.name}`);
      load();
    } catch (e) { toast.error(errorMessage(e, "Visit failed")); }
    finally { setBusy(null); }
  };

  const visitNfc = async (stop) => {
    if (!nfcTag.trim()) { toast.error("Enter or scan a tag id."); return; }
    setBusy(`${stop.id}:nfc`);
    try {
      await api.post("/portal/tour-visits", { shift_id: shiftId, stop_id: stop.id, method: "nfc", nfc_tag_id: nfcTag.trim() });
      toast.success(`NFC-tagged ${stop.name}`);
      setNfcShownFor(null);
      setNfcTag("");
      load();
    } catch (e) { toast.error(errorMessage(e, "NFC visit failed")); }
    finally { setBusy(null); }
  };

  // Web NFC scan (Chrome Android over HTTPS)
  const scanNfc = async (stop) => {
    if (!("NDEFReader" in window)) {
      // Fallback: surface manual input
      setNfcShownFor(stop.id);
      return;
    }
    try {
      const reader = new window.NDEFReader();
      await reader.scan();
      toast.info("Tap the NFC tag on this stop…");
      reader.onreading = async (event) => {
        const serial = event.serialNumber || "";
        setNfcTag(serial);
        // Auto-submit
        try {
          await api.post("/portal/tour-visits", { shift_id: shiftId, stop_id: stop.id, method: "nfc", nfc_tag_id: serial });
          toast.success(`NFC-tagged ${stop.name}`);
          load();
        } catch (e) { toast.error(errorMessage(e, "NFC visit failed")); }
      };
    } catch (e) {
      // Permission denied / unsupported — fall back to manual entry
      setNfcShownFor(stop.id);
    }
  };

  if (loading) {
    return (
      <div className="border border-[#27272A] bg-[#121212] p-4 text-center text-xs font-mono text-zinc-500">
        <Loader2 className="size-4 animate-spin inline mr-2" /> loading tour stops…
      </div>
    );
  }
  if (stops.length === 0) {
    return null; // hide entirely if no stops configured
  }

  return (
    <section data-testid="portal-tour-stops">
      <div className="label-mute">Patrol</div>
      <h2 className="font-display text-2xl uppercase font-bold tracking-tight mb-4">Tour Stops</h2>
      <div className="space-y-3">
        {stops.map((s) => {
          const tone = nextDueTone(s);
          return (
            <div key={s.id} data-testid={`portal-tour-stop-${s.id}`} className="border border-[#27272A] bg-[#121212] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase font-bold tracking-tight truncate">{s.name}</div>
                  {s.instructions && <div className="text-xs text-zinc-400 mt-1">{s.instructions}</div>}
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                    every {s.frequency_minutes}m · last visit: <span className="text-zinc-300">{fmtRelative(s.last_visit_at)}</span>
                  </div>
                </div>
                <PillBadge tone={tone}>
                  {tone === "red" ? "overdue" : tone === "amber" ? (s.last_visit_at ? "due soon" : "pending") : "ok"}
                </PillBadge>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 justify-end">
                <ActionButton data-testid={`portal-visit-gps-${s.id}`} disabled={busy === `${s.id}:gps`} onClick={() => visitGps(s)}>
                  {busy === `${s.id}:gps` ? <Loader2 className="size-4 animate-spin" /> : <MapPin className="size-4" />}
                  Log Visit (GPS)
                </ActionButton>
                {s.nfc_tag_id && (
                  <GhostButton data-testid={`portal-visit-nfc-${s.id}`} disabled={busy === `${s.id}:nfc`} onClick={() => scanNfc(s)}>
                    <Radio className="size-3.5" /> Scan NFC
                  </GhostButton>
                )}
              </div>
              {nfcShownFor === s.id && (
                <div className="mt-3 border-t border-[#27272A] pt-3 flex items-center gap-2" data-testid={`portal-nfc-manual-${s.id}`}>
                  <input
                    autoFocus
                    type="text"
                    value={nfcTag}
                    onChange={(e) => setNfcTag(e.target.value)}
                    placeholder="Enter NFC tag id (Web NFC unavailable)"
                    className="flex-1 bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] outline-none px-3 py-2 text-sm font-mono"
                  />
                  <ActionButton onClick={() => visitNfc(s)}><Check className="size-4" /> Log</ActionButton>
                  <GhostButton onClick={() => { setNfcShownFor(null); setNfcTag(""); }}><AlertOctagon className="size-3.5" /></GhostButton>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
